import React, { useMemo } from 'react';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import FAIcon from '#/common/components/FAIcon';
import { DashboardStatsPoint } from '#/graphql/types';
import { NOT_ASSIGNED_LABEL } from '../../utils/constants';

const containerStyles = `
  .dashboard-cards-container {
    display: flex;
    flex-wrap: wrap;
    gap: 40px;
    justify-content: flex-start;
    align-items: flex-start;
  }

  .dashboard-card {
    flex: 1 1 calc(50% - 20px);
    min-width: 150px;
    max-width: calc(50% - 20px);
  }

  @media (max-width: 560px) {
    .dashboard-cards-container {
      flex-direction: column;
      gap: 20px;
    }

    .dashboard-card {
      flex: 1 1 100% !important;
      max-width: 100% !important;
      min-width: auto !important;
    }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('dashboard-cards-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'dashboard-cards-styles';
  styleElement.textContent = containerStyles;
  document.head.appendChild(styleElement);
}

export interface DashboardCardsProps {
  dataSource: DashboardStatsPoint[] | undefined | null;
  title?: string;
  subTitle?: string;
  noDataText?: string;
  colors: Record<string, string>;
  onItemClick?: (point: DashboardStatsPoint) => void;
}

const DashboardCards: React.FC<DashboardCardsProps> = ({
  dataSource,
  title,
  subTitle,
  noDataText = 'No data to display!',
  colors,
  onItemClick,
}) => {
  const ds = useMemo(
    () =>
      dataSource
        ?.filter((i) => !!i)
        .map((i) => ({
          ...i,
          value: i.value || 0,
          key: i.filterByValue || i.label || NOT_ASSIGNED_LABEL,
        })) || [],
    [dataSource],
  );

  const getBackgroundColor = (key: string): string => {
    const textColor = getTitleTextColor(key);

    // Convert hex to rgba with 0.3 opacity
    const hexToRgba = (hex: string, alpha: number = 0.3): string => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return hexToRgba(textColor, 0.3);
  };

  const getTitleTextColor = (key: string): string => {
    const normalizedKey = key?.toLowerCase().trim() || '';

    // Check for custom color definitions first
    return colors[normalizedKey] || '#6A6C71'; // Fallback matches ui.dark
  };

  const handleCardClick = (item: DashboardStatsPoint) => {
    if (onItemClick) {
      onItemClick(item);
    }
  };

  if (!ds || ds.length === 0) {
    return (
      <NuvoBox
        height="100%"
        minHeight="300px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        flex={1}>
        <FAIcon icon="circle-info" size="2xl" />
        <NuvoText type="body">{noDataText}</NuvoText>
      </NuvoBox>
    );
  }

  return (
    <NuvoBox>
      {title && (
        <NuvoBox marginBottom={2}>
          <NuvoText type="header3">{title}</NuvoText>
          {subTitle && <NuvoText type="body">{subTitle}</NuvoText>}
        </NuvoBox>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '40px',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
        }}
        className="dashboard-cards-container">
        {ds.map((item, index) => (
          <div
            key={`${item.key}-${index}`}
            onClick={() => handleCardClick(item)}
            className="dashboard-card"
            style={{
              cursor: onItemClick ? 'pointer' : 'default',
              height: '180px',
              width: '100%',
              backgroundColor: getBackgroundColor(item.key || ''),
              borderRadius: '24px',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <div
              className="card-wrapper"
              style={{ display: 'flex', flexDirection: 'column', padding: 0, width: '100%' }}>
              <div className="card-body">
                <NuvoBox>
                  <div
                    style={{
                      fontSize: '28px',
                      fontWeight: 'bold',
                      color: getTitleTextColor(item.key || ''),
                    }}>
                    {typeof item.value === 'number'
                      ? item.value.toLocaleString()
                      : item.value || '0'}
                  </div>
                </NuvoBox>
              </div>
              <div
                style={{
                  fontWeight: 400,
                  fontSize: '16px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                  marginTop: '16px',
                }}
                title={item.label || NOT_ASSIGNED_LABEL}>
                {item.label || NOT_ASSIGNED_LABEL}
              </div>
            </div>
          </div>
        ))}
      </div>
    </NuvoBox>
  );
};

export default React.memo(DashboardCards);
